import { DeviceLogEntry } from './deviceLogs';

/**
 * Setup summary (FS-1186): the facts an operator would otherwise piece
 * together by hand from a setup session's timeline. Did the scan find the
 * Clip, how long did the BLE connect and the WiFi join take, what did the
 * Clip answer, did the password change between tries, did the cloud confirm.
 * A pure function over the session's entries, like SessionFacts; nothing
 * here fetches.
 *
 * Each milestone is recognised by its Plan 104 `ctx.event` name when the entry
 * has one and by its message text otherwise, so sessions from app builds
 * before FS-1185 (which adds the events) summarize the same way. The WiFi
 * reply time uses the 0x09 reply's `durationMs` when the app logged one
 * (FS-1185) and the timestamp difference otherwise. A fact whose inputs are
 * missing is null, never a failure: the view says "not logged".
 */

/** Values of `ctx.status` on the WiFi credential reply, as the app logs them. */
export const WIFI_STATUS = {
  success: 'success',
  noConnection: 'noConnection',
  badCredentials: 'badCredentials',
  serverNotFound: 'serverNotFound',
  timeout: 'timeout',
  writeFailed: 'writeFailed',
} as const;

export interface WifiTry {
  sentTs: string;
  ssid: string | null;
  credentialSource: string | null;
  credentialFingerprint: string | null;
  /** True when the fingerprint differs from the previous try in the session; null when either is unknown. */
  credentialChanged: boolean | null;
  /** `ctx.status` of the reply (see WIFI_STATUS); null when no status was logged. */
  status: string | null;
  /** The Clip's WiFi status code (its wifi_status_t, as hex) from the reply payload, when the raw bytes or a `code` field were logged. */
  replyCode: string | null;
  /** True when the Clip answered; false when the app gave up waiting or the write failed. */
  replied: boolean;
  replyMs: number | null;
  replySource: 'logged' | 'computed' | null;
}

export type AttemptOutcome =
  | 'cloud_confirmed'
  | 'proceeded_unconfirmed'
  | 'wifi_joined_unconfirmed'
  | 'wifi_failed'
  | 'wifi_skipped'
  | 'stopped_after_connect'
  | 'found_not_connected'
  | 'no_clip_found'
  | 'bluetooth_off'
  | 'no_scan';

export interface SetupAttempt {
  index: number;
  startTs: string;
  endTs: string;
  bluetooth: 'on' | 'off' | 'off_then_on' | null;
  blePermission: 'granted' | 'denied' | 'denied_then_granted' | null;
  scans: number;
  emptyScans: number;
  clipFound: boolean;
  targetsFound: number | null;
  totalSeen: number | null;
  rssi: number | null;
  clipName: string | null;
  clipSerial: string | null;
  connected: boolean;
  connectMs: number | null;
  connectAttempt: number | null;
  connectMaxAttempts: number | null;
  pairingRetries: number;
  statusTimeouts: number;
  disconnects: number;
  firmware: string | null;
  mode: string | null;
  statusFlags: string | null;
  /** The Clip's clock as read by the first status request, before the app syncs it. */
  clipClock: string | null;
  /** True when that clock was still the firmware's cold-boot default, so the Clip had just powered on. */
  clockAtDefault: boolean | null;
  secondsSinceBoot: number | null;
  wifiSkipped: boolean;
  wifiTries: WifiTry[];
  cloudConfirmed: boolean;
  /** Milliseconds from the attempt's start to the cloud confirmation. */
  confirmMs: number | null;
  verifyProceeded: 'verified' | 'skipped' | null;
  outcome: AttemptOutcome;
}

export interface SetupSummaryResult {
  attempts: SetupAttempt[];
  /** WiFi screen activity before the first setup attempt (the app collects credentials first). */
  credentialsEnteredBefore: number;
  wifiSkippedBefore: number;
  /** False for a session with no setup activity at all; the view renders nothing then. */
  hasActivity: boolean;
}

type Marker =
  | 'setup_started'
  | 'adapter_state'
  | 'ble_permission'
  | 'scan_started'
  | 'target_found'
  | 'scan_completed'
  | 'scan_empty'
  | 'device_selected'
  | 'connect_started'
  | 'connected'
  | 'pairing_retry'
  | 'status_timeout'
  | 'device_status'
  | 'disconnected'
  | 'credentials_submitted'
  | 'wifi_skipped'
  | 'credentials_sent'
  | 'wifi_reply'
  | 'wifi_reply_raw'
  | 'wifi_result'
  | 'wifi_failed'
  | 'wifi_timeout'
  | 'wifi_write_failed'
  | 'verify_confirmed'
  | 'verify_proceeded';

/** Plan 104 event names (the FS-1185 additions included) and what they mark. */
const BY_EVENT: Record<string, Marker> = {
  'setup.flow.started': 'setup_started',
  'ble.adapter.state_changed': 'adapter_state',
  'ble.permission.checked': 'ble_permission',
  'ble.scan.started': 'scan_started',
  'ble.scan.target_found': 'target_found',
  'ble.scan.completed': 'scan_completed',
  'ble.device.selected': 'device_selected',
  'ble.connect.started': 'connect_started',
  'ble.connect.succeeded': 'connected',
  'wifi.credentials.submitted': 'credentials_submitted',
  'wifi.setup.skipped': 'wifi_skipped',
  'wifi.credentials.sent': 'credentials_sent',
  'wifi.provision.result': 'wifi_result',
  'setup.verify.confirmed': 'verify_confirmed',
  'setup.verify.proceeded': 'verify_proceeded',
};

const WIFI_OPCODE = '0x09';
const STATUS_OPCODE = '0x01';

function markerForEvent(event: string, e: DeviceLogEntry): Marker | null {
  const opcode = ctxString(e.ctx, 'opcode');
  switch (event) {
    case 'ble.opcode.timeout':
      if (opcode === WIFI_OPCODE) return 'wifi_timeout';
      if (opcode === STATUS_OPCODE) return 'status_timeout';
      return null;
    case 'ble.opcode.response':
      return opcode === WIFI_OPCODE ? 'wifi_reply' : null;
    case 'ble.opcode.write_failed':
      return opcode === WIFI_OPCODE ? 'wifi_write_failed' : null;
    default:
      return BY_EVENT[event] ?? null;
  }
}

const msgIs = (text: string) => (e: DeviceLogEntry) => e.msg === text;
const msgStarts = (text: string) => (e: DeviceLogEntry) => e.msg.startsWith(text);

/** Message text of each milestone in app builds that log it without a `ctx.event`. */
const BY_MESSAGE: Array<[(e: DeviceLogEntry) => boolean, Marker]> = [
  [msgIs('setup flow started'), 'setup_started'],
  [msgIs('Bluetooth adapter state'), 'adapter_state'],
  [msgIs('Bluetooth permission check'), 'ble_permission'],
  [msgIs('scan starting'), 'scan_started'],
  [msgIs('found target device'), 'target_found'],
  [msgIs('scan completed'), 'scan_completed'],
  [msgIs('scan completed with no FluxHive Clips found'), 'scan_empty'],
  [msgIs('Clip selected for connection'), 'device_selected'],
  [msgIs('connect starting'), 'connect_started'],
  [(e) => e.source === 'ble' && e.msg === 'connected', 'connected'],
  [msgStarts('pairing in flight'), 'pairing_retry'],
  [msgIs('device status timed out'), 'status_timeout'],
  [msgIs('opcode 0x01 device status received'), 'device_status'],
  [(e) => e.source === 'ble' && e.msg === 'disconnected', 'disconnected'],
  [msgIs('WiFi credentials submitted'), 'credentials_submitted'],
  [msgIs('WiFi setup skipped'), 'wifi_skipped'],
  [msgStarts('sending WiFi credentials'), 'credentials_sent'],
  [msgStarts('opcode 0x09 writing credentials'), 'credentials_sent'],
  [msgIs('opcode 0x09 response (raw)'), 'wifi_reply_raw'],
  [msgIs('opcode 0x09 result'), 'wifi_result'],
  [msgIs('WiFi config failed'), 'wifi_failed'],
  [msgIs('WiFi credential response timed out'), 'wifi_timeout'],
  [msgStarts('opcode 0x09 failed'), 'wifi_write_failed'],
  [msgIs('verify: Clip confirmed with cloud'), 'verify_confirmed'],
  [(e) => e.msg === 'verify: iotActivationConfirmed' && ctxBool(e.ctx, 'confirmed') === true, 'verify_confirmed'],
];

function markerOf(e: DeviceLogEntry): Marker | null {
  const event = ctxString(e.ctx, 'event');
  if (event) {
    const marker = markerForEvent(event, e);
    if (marker) return marker;
  }
  for (const [matches, marker] of BY_MESSAGE) {
    if (matches(e)) return marker;
  }
  return null;
}

function ctxString(ctx: Record<string, unknown> | null, key: string): string | null {
  const v = ctx?.[key];
  return typeof v === 'string' ? v : null;
}

function ctxNumber(ctx: Record<string, unknown> | null, key: string): number | null {
  const v = ctx?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function ctxBool(ctx: Record<string, unknown> | null, key: string): boolean | null {
  const v = ctx?.[key];
  return typeof v === 'boolean' ? v : null;
}

function millis(ts: string): number | null {
  const t = Date.parse(ts);
  return Number.isNaN(t) ? null : t;
}

function elapsedMs(from: string, to: string): number | null {
  const a = millis(from);
  const b = millis(to);
  return a === null || b === null ? null : b - a;
}

/**
 * A Clip reply frame is `bb aa 01 f5 <opcode> <command status> <payload>`. For
 * opcode 0x09 the payload byte is the Clip's WiFi status code; the command
 * status byte before it is not (0x04 means "error, see payload").
 */
const REPLY_OPCODE_INDEX = 4;
const REPLY_PAYLOAD_INDEX = 6;
const WIFI_OPCODE_BYTE = '09';

function wifiCodeFromHex(hex: string | null): string | null {
  if (!hex) return null;
  const bytes = hex.trim().split(/\s+/);
  if (bytes[REPLY_OPCODE_INDEX] !== WIFI_OPCODE_BYTE || bytes.length <= REPLY_PAYLOAD_INDEX) return null;
  return `0x${bytes[REPLY_PAYLOAD_INDEX]}`;
}

/**
 * The Clip's RTC starts at this value on a cold boot (firmware
 * `Src/Drivers/rtc_driver.h`: RTC_DEFAULT_YEAR 25, January 1, RTC_DEFAULT_HOUR
 * 14, RTC_DEFAULT_MIN 10, RTC_DEFAULT_SEC 52) and runs from it until the app
 * syncs the time over BLE, so a status read showing a time within a day of
 * it means the Clip powered on that many seconds earlier.
 */
const RTC_COLD_BOOT_MS = Date.parse('2025-01-01T14:10:52Z');
const ONE_DAY_MS = 86_400_000;

function clockFacts(time: string | null): { clockAtDefault: boolean | null; secondsSinceBoot: number | null } {
  if (!time) return { clockAtDefault: null, secondsSinceBoot: null };
  const t = Date.parse(`${time.replace(' ', 'T')}Z`);
  if (Number.isNaN(t)) return { clockAtDefault: null, secondsSinceBoot: null };
  const atDefault = t >= RTC_COLD_BOOT_MS && t < RTC_COLD_BOOT_MS + ONE_DAY_MS;
  return {
    clockAtDefault: atDefault,
    secondsSinceBoot: atDefault ? Math.round((t - RTC_COLD_BOOT_MS) / 1000) : null,
  };
}

/** The credentials most recently entered on the WiFi screen, carried into each write. */
interface CredentialState {
  ssid: string | null;
  source: string | null;
  fingerprint: string | null;
}

/** A WiFi try still waiting for the Clip's reply. */
interface OpenTry {
  value: WifiTry;
  /** When the reply, the timeout or the write failure was logged: the end of the measured wait. */
  replyTs: string | null;
  loggedMs: number | null;
  replied: boolean;
}

interface Draft {
  attempt: SetupAttempt;
  emptyFromCompleted: number;
  emptyMessages: number;
  scanCompletions: number;
  firstStatusRead: boolean;
  openTry: OpenTry | null;
}

function newDraft(index: number, startTs: string): Draft {
  return {
    attempt: {
      index,
      startTs,
      endTs: startTs,
      bluetooth: null,
      blePermission: null,
      scans: 0,
      emptyScans: 0,
      clipFound: false,
      targetsFound: null,
      totalSeen: null,
      rssi: null,
      clipName: null,
      clipSerial: null,
      connected: false,
      connectMs: null,
      connectAttempt: null,
      connectMaxAttempts: null,
      pairingRetries: 0,
      statusTimeouts: 0,
      disconnects: 0,
      firmware: null,
      mode: null,
      statusFlags: null,
      clipClock: null,
      clockAtDefault: null,
      secondsSinceBoot: null,
      wifiSkipped: false,
      wifiTries: [],
      cloudConfirmed: false,
      confirmMs: null,
      verifyProceeded: null,
      outcome: 'no_scan',
    },
    emptyFromCompleted: 0,
    emptyMessages: 0,
    scanCompletions: 0,
    firstStatusRead: false,
    openTry: null,
  };
}

function closeTry(draft: Draft): void {
  const open = draft.openTry;
  if (!open) return;
  const t = open.value;
  if (open.loggedMs !== null) {
    t.replyMs = open.loggedMs;
    t.replySource = 'logged';
  } else if (open.replyTs) {
    t.replyMs = elapsedMs(t.sentTs, open.replyTs);
    t.replySource = t.replyMs === null ? null : 'computed';
  }
  t.replied = open.replied;
  draft.attempt.wifiTries.push(t);
  draft.openTry = null;
}

function outcomeOf(a: SetupAttempt): AttemptOutcome {
  if (a.cloudConfirmed) return 'cloud_confirmed';
  if (a.verifyProceeded === 'skipped') return 'proceeded_unconfirmed';
  if (a.wifiTries.some((t) => t.status === WIFI_STATUS.success)) return 'wifi_joined_unconfirmed';
  if (a.wifiTries.length > 0) return 'wifi_failed';
  if (a.wifiSkipped) return 'wifi_skipped';
  if (a.connected) return 'stopped_after_connect';
  if (a.clipFound) return 'found_not_connected';
  if (a.scans > 0) return 'no_clip_found';
  if (a.bluetooth === 'off') return 'bluetooth_off';
  return 'no_scan';
}

function finish(draft: Draft): SetupAttempt {
  closeTry(draft);
  const a = draft.attempt;
  // Builds that log both the "no Clips found" warning and the completed-scan
  // event would count the same empty scan twice, so the event wins when present.
  a.emptyScans = draft.scanCompletions > 0 ? draft.emptyFromCompleted : draft.emptyMessages;
  if (a.scans === 0) a.scans = Math.max(draft.scanCompletions, draft.emptyMessages);
  a.outcome = outcomeOf(a);
  return a;
}

function applyAdapterState(a: SetupAttempt, state: string | null): void {
  if (state !== 'on' && state !== 'off') return;
  if (a.bluetooth === null) a.bluetooth = state;
  else if (a.bluetooth === 'off' && state === 'on') a.bluetooth = 'off_then_on';
}

function applyPermission(a: SetupAttempt, granted: boolean | null): void {
  if (granted === null) return;
  const state = granted ? 'granted' : 'denied';
  if (a.blePermission === null) a.blePermission = state;
  else if (a.blePermission === 'denied' && granted) a.blePermission = 'denied_then_granted';
}

function applyDeviceStatus(draft: Draft, e: DeviceLogEntry): void {
  const a = draft.attempt;
  a.firmware = ctxString(e.ctx, 'firmware') ?? a.firmware;
  a.mode = ctxString(e.ctx, 'mode') ?? a.mode;
  a.statusFlags = ctxString(e.ctx, 'statusFlags') ?? a.statusFlags;
  a.clipSerial = ctxString(e.ctx, 'serial') ?? a.clipSerial;
  a.clipName = a.clipName ?? ctxString(e.ctx, 'deviceName');
  // Only the first read shows the Clip's own clock; the app syncs it right after.
  if (draft.firstStatusRead) return;
  draft.firstStatusRead = true;
  a.clipClock = ctxString(e.ctx, 'time');
  const clock = clockFacts(a.clipClock);
  a.clockAtDefault = clock.clockAtDefault;
  a.secondsSinceBoot = clock.secondsSinceBoot;
}

/** Two log lines describe the same credential write; a second line within this window is not a new try. */
const SAME_WRITE_WINDOW_MS = 2000;

function startTry(draft: Draft, e: DeviceLogEntry, cred: CredentialState, previousFingerprint: string | null): void {
  const open = draft.openTry;
  if (open) {
    const gap = elapsedMs(open.value.sentTs, e.ts);
    if (gap !== null && gap >= 0 && gap < SAME_WRITE_WINDOW_MS && open.replyTs === null) return;
    closeTry(draft);
  }
  const fingerprint = cred.fingerprint;
  draft.openTry = {
    value: {
      sentTs: e.ts,
      ssid: ctxString(e.ctx, 'ssid') ?? cred.ssid,
      credentialSource: cred.source,
      credentialFingerprint: fingerprint,
      credentialChanged:
        fingerprint === null || previousFingerprint === null ? null : fingerprint !== previousFingerprint,
      status: null,
      replyCode: null,
      replied: false,
      replyMs: null,
      replySource: null,
    },
    replyTs: null,
    loggedMs: null,
    replied: false,
  };
}

/** Ends the wait of the open try. `isReply` is false for the app's own timeout and for a failed write. */
function applyReply(draft: Draft, e: DeviceLogEntry, status: string | null, close: boolean, isReply = true): void {
  const open = draft.openTry;
  if (!open) return;
  open.replyTs = open.replyTs ?? e.ts;
  if (isReply) open.replied = true;
  const logged = ctxNumber(e.ctx, 'durationMs');
  if (logged !== null) open.loggedMs = logged;
  if (status !== null && open.value.status === null) open.value.status = status;
  if (close) closeTry(draft);
}

function applyVerifyConfirmed(a: SetupAttempt, ts: string): void {
  if (a.cloudConfirmed) return;
  a.cloudConfirmed = true;
  a.confirmMs = elapsedMs(a.startTs, ts);
}

export function summarizeSetup(entries: DeviceLogEntry[]): SetupSummaryResult {
  const ordered = [...entries].sort((x, y) => x.seq - y.seq || x.ts.localeCompare(y.ts));
  const attempts: SetupAttempt[] = [];
  let draft: Draft | null = null;
  let credentialsEnteredBefore = 0;
  let wifiSkippedBefore = 0;
  const cred: CredentialState = { ssid: null, source: null, fingerprint: null };
  // Fingerprint of the last credential write anywhere in the session, so a
  // changed password shows up across attempts as well as within one.
  let lastWrittenFingerprint: string | null = null;

  for (const e of ordered) {
    if (draft) draft.attempt.endTs = e.ts;
    const marker = markerOf(e);
    if (!marker) continue;

    if (marker === 'setup_started') {
      if (draft) attempts.push(finish(draft));
      draft = newDraft(attempts.length + 1, e.ts);
      continue;
    }
    if (marker === 'credentials_submitted') {
      cred.ssid = ctxString(e.ctx, 'ssid') ?? cred.ssid;
      cred.source = ctxString(e.ctx, 'credentialSource');
      cred.fingerprint = ctxString(e.ctx, 'credentialFingerprint');
      if (!draft) credentialsEnteredBefore += 1;
      continue;
    }
    if (marker === 'wifi_skipped') {
      if (draft) draft.attempt.wifiSkipped = true;
      else wifiSkippedBefore += 1;
      continue;
    }
    if (!draft) continue;
    const a = draft.attempt;

    switch (marker) {
      case 'adapter_state':
        applyAdapterState(a, ctxString(e.ctx, 'state'));
        break;
      case 'ble_permission':
        applyPermission(a, ctxBool(e.ctx, 'granted') ?? (ctxString(e.ctx, 'result') === 'success' ? true : null));
        break;
      case 'scan_started':
        a.scans += 1;
        break;
      case 'target_found':
        a.clipFound = true;
        a.rssi = ctxNumber(e.ctx, 'rssi') ?? a.rssi;
        a.clipName = ctxString(e.ctx, 'name') ?? a.clipName;
        break;
      case 'scan_completed': {
        draft.scanCompletions += 1;
        const found = ctxNumber(e.ctx, 'targetsFound');
        const seen = ctxNumber(e.ctx, 'totalSeen');
        if (found === 0) draft.emptyFromCompleted += 1;
        // Keep the scan that found the Clip; otherwise the latest one.
        if (a.targetsFound === null || a.targetsFound === 0 || (found !== null && found > 0)) {
          a.targetsFound = found;
          a.totalSeen = seen;
        }
        break;
      }
      case 'scan_empty':
        draft.emptyMessages += 1;
        break;
      case 'device_selected':
        a.clipName = ctxString(e.ctx, 'name') ?? a.clipName;
        a.rssi = ctxNumber(e.ctx, 'rssi') ?? a.rssi;
        break;
      case 'connect_started':
        a.connectAttempt = ctxNumber(e.ctx, 'attempt') ?? a.connectAttempt;
        a.connectMaxAttempts = ctxNumber(e.ctx, 'maxAttempts') ?? a.connectMaxAttempts;
        break;
      case 'connected':
        a.connected = true;
        a.connectMs = ctxNumber(e.ctx, 'durationMs');
        a.clipName = ctxString(e.ctx, 'device') ?? a.clipName;
        break;
      case 'pairing_retry':
        a.pairingRetries += 1;
        break;
      case 'status_timeout':
        a.statusTimeouts += 1;
        break;
      case 'device_status':
        applyDeviceStatus(draft, e);
        break;
      case 'disconnected':
        a.disconnects += 1;
        break;
      case 'credentials_sent':
        startTry(draft, e, cred, lastWrittenFingerprint);
        if (draft.openTry) lastWrittenFingerprint = draft.openTry.value.credentialFingerprint ?? lastWrittenFingerprint;
        break;
      case 'wifi_reply':
        applyReply(draft, e, null, false);
        break;
      case 'wifi_reply_raw':
        if (draft.openTry) {
          draft.openTry.value.replyCode = wifiCodeFromHex(ctxString(e.ctx, 'hex')) ?? draft.openTry.value.replyCode;
        }
        break;
      case 'wifi_result':
      case 'wifi_failed':
        if (draft.openTry) draft.openTry.value.replyCode = ctxString(e.ctx, 'code') ?? draft.openTry.value.replyCode;
        applyReply(draft, e, ctxString(e.ctx, 'status'), true);
        break;
      case 'wifi_timeout':
        applyReply(draft, e, WIFI_STATUS.timeout, true, false);
        break;
      case 'wifi_write_failed':
        applyReply(draft, e, WIFI_STATUS.writeFailed, true, false);
        break;
      case 'verify_confirmed':
        applyVerifyConfirmed(a, e.ts);
        break;
      case 'verify_proceeded':
        if (ctxBool(e.ctx, 'verified') === true) {
          a.verifyProceeded = 'verified';
          applyVerifyConfirmed(a, e.ts);
        } else if (ctxBool(e.ctx, 'skipped') === true) {
          a.verifyProceeded = 'skipped';
        }
        break;
    }
  }
  if (draft) attempts.push(finish(draft));

  return {
    attempts,
    credentialsEnteredBefore,
    wifiSkippedBefore,
    hasActivity: attempts.length > 0 || credentialsEnteredBefore > 0 || wifiSkippedBefore > 0,
  };
}

/** "546 ms", "6.0 s", "1 min 12 s". */
export function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes} min ${seconds} s`;
}
